import Tiptap from '@components/Tiptap';
import { serializeTelegramHtml } from '@components/Tiptap/serialize-telegram-html';
import toFormData from '@helpers/form-data';
import useAttachments from '@hooks/use-attachments';
import useAuth from '@hooks/use-auth';
import useLastCallback from '@hooks/use-last-callback';
import type { Attachment } from '@interfaces/attachment';
import AttachmentList from '@pages/components/AttachmentList';
import { apiRequest, isResponse } from '@services/fetcher';
import { ValidationError } from '@services/types/validation-error';
import { validateFiles } from '@utils/file';
import { openSystemFilesDialog } from '@utils/ui/system-files-dialog';
import { Button, Checkbox, Form, message, Modal, Tooltip } from 'antd';
import type { ParsedQs } from 'qs';
import type { FC } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import styles from './BulkMessageForm.module.scss';
import { BULK_MESSAGE_LIMIT, getBulkMessageContent } from './message-content';

interface OwnProps {
  open: boolean;
  onSent: () => void;
  onCancel: () => void;
  filters?: ParsedQs;
  isFormDisabled?: boolean;
}

interface Values {
  slowMode: boolean;
  message?: string;
}

const BulkMessageForm: FC<OwnProps> = ({ open, onCancel, onSent, filters, isFormDisabled }) => {
  const auth = useAuth();
  const [form] = Form.useForm<Values>();
  const [isQueueing, setIsQueueing] = useState(false);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [hasError, setHasError] = useState<boolean>(false);
  const messageText = Form.useWatch('message', form) as Values['message'];
  const content = useMemo(() => getBulkMessageContent(messageText), [messageText]);
  const hasContent = content.hasText || attachments.length > 0;

  useEffect(() => {
    if (form.getFieldError('message').length) {
      void form.validateFields(['message']).catch(() => undefined);
    }
  }, [attachments.length, form]);

  const queueMessageSending = useCallback(
    async (message: string | undefined, slowMode: boolean, attachments: Attachment[], filters?: ParsedQs) => {
      const formData = toFormData({
        message: getBulkMessageContent(message).hasText ? message : undefined,
        slowMode,
        filter: filters,
      });
      for (const attachment of attachments) {
        formData.append('files', attachment.file);
      }
      const response = await apiRequest(`${import.meta.env.VITE_API_URL}/manage/telegram-group/message`, auth, {
        method: 'post',
        body: formData,
      });

      if (response instanceof Error) {
        throw response;
      }
      if (isResponse(response) && !response.ok) {
        throw new Error(response.statusText || 'Failed to queue bulk message.');
      }
    },
    [auth],
  );

  const { handleAppendFiles, handleFileSelect, handleClearAttachments, handleSetAttachments } = useAttachments(
    attachments,
    setAttachments,
  );

  const onTriggerClose = useCallback(() => {
    form.resetFields();
    handleClearAttachments();
    onCancel();
  }, [form, handleClearAttachments, onCancel]);

  const onSubmit = useCallback(
    async (values: Values) => {
      if (isFormDisabled || hasError || isQueueing || !hasContent) {
        return;
      }

      setIsQueueing(true);
      try {
        await queueMessageSending(values.message, values.slowMode, attachments, filters);
        form.resetFields();
        handleClearAttachments();
        onSent();
      } catch (error) {
        if (error instanceof ValidationError && error.details?.message?.length) {
          form.setFields([{ name: 'message', errors: error.details.message }]);
        } else {
          message.error(error instanceof Error ? error.message : 'Failed to queue bulk message.');
        }
      } finally {
        setIsQueueing(false);
      }
    },
    [
      isFormDisabled,
      hasError,
      isQueueing,
      hasContent,
      form,
      queueMessageSending,
      filters,
      handleClearAttachments,
      onSent,
      attachments,
    ],
  );

  const onFileSelect = useLastCallback((e: Event) => {
    const { files } = e.target as HTMLInputElement;
    const validatedFiles = validateFiles(files);

    if (!validatedFiles?.length) {
      return;
    }

    if (attachments.length) {
      // noinspection JSIgnoredPromiseFromCall
      handleAppendFiles(validatedFiles);
    } else {
      // noinspection JSIgnoredPromiseFromCall
      handleFileSelect(validatedFiles);
    }
  });

  const handleDocumentSelect = useLastCallback(() => {
    openSystemFilesDialog('*', (e) => onFileSelect(e));
  });

  const onAttachmentError = useLastCallback((error?: string) => {
    setHasError(error !== undefined);
  });

  return (
    <Modal
      title="Send bulk message"
      open={open}
      onCancel={onTriggerClose}
      closable={!isQueueing}
      maskClosable={!isQueueing}
      keyboard={!isQueueing}
      footer={[
        <Button key="attach" type="default" onClick={handleDocumentSelect} disabled={isQueueing}>
          {attachments.length ? 'Add Files' : 'Attach Files'}
        </Button>,
        <Button key="cancel" type="default" onClick={onTriggerClose} disabled={isQueueing}>
          Cancel
        </Button>,
        <Button
          key="send"
          type="primary"
          onClick={() => form.submit()}
          disabled={isFormDisabled || hasError || isQueueing || !hasContent}
          loading={isQueueing}
        >
          Send
        </Button>,
      ]}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={onSubmit}
        disabled={isQueueing}
        initialValues={{ message: '', slowMode: false }}
      >
        <Form.Item<Values>
          name="message"
          extra={
            <div style={{ paddingTop: 16 }}>
              <div>
                {content.length} / {BULK_MESSAGE_LIMIT}
              </div>
              Enter a message or attach files. Select text to format it. Sent to filtered conversations.
            </div>
          }
          rules={[
            {
              validator: (_, value: string | undefined) => {
                const { hasText, length } = getBulkMessageContent(value);
                if (!hasText && attachments.length === 0) {
                  return Promise.reject(new Error('Please enter a message or attach at least one file.'));
                }
                if (hasText && length > BULK_MESSAGE_LIMIT) {
                  return Promise.reject(new Error(`Message can not exceed ${BULK_MESSAGE_LIMIT} characters.`));
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          <Tiptap
            serializeHtml={serializeTelegramHtml}
            classNames={{ textarea: styles.editor }}
            style={{ minHeight: 120 }}
            aria-label="Bulk message"
          />
        </Form.Item>
        <Form.Item<Values> name="slowMode" valuePropName="checked">
          <Checkbox>
            <Tooltip title="When enabled, messages are sent one at a time, approximately once every 10 seconds.">
              Slow mode
            </Tooltip>
          </Checkbox>
        </Form.Item>
      </Form>
      <AttachmentList
        attachments={attachments}
        onAttachmentsAppend={isQueueing ? undefined : handleAppendFiles}
        onAttachmentsUpdate={isQueueing ? undefined : handleSetAttachments}
        onError={onAttachmentError}
      />
    </Modal>
  );
};

export default BulkMessageForm;
